import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  AdminAccountFilters,
  createAdminAccountRequest,
  createAdminCleanerRequest,
  deleteAdminAccountRequest,
  deleteAdminUserRequest,
  getAdminAccountRequest,
  getAdminAccountsRequest,
  getAdminSubscriptionsRequest,
  updateAdminSubscriptionRequest,
  updateAdminUserStatusRequest,
  createAdminPropertyRequest,
  createAdminUnitRequest,
  updateAdminPropertyRequest,
  updateAdminUnitRequest,
} from "../../api/adminAccountsApi";
import { CreateAdminAccountRequest } from "../../models/RequestModels/CreateAdminAccountRequest";
import { CreateAdminCleanerRequest } from "../../models/RequestModels/CreateAdminCleanerRequest";
import { UpdateAdminSubscriptionRequest } from "../../models/RequestModels/UpdateAdminSubscriptionRequest";
import { AdminAccountResponse } from "../../models/ResponseModels/AdminAccountResponse";
import { AdminSubscriptionResponse } from "../../models/ResponseModels/AdminSubscriptionResponse";
import { PaginatedResponse } from "../../models/ResponseModels/PaginatedResponse";
import {
  ApiRejectPayload,
  FieldErrors,
  getApiErrorPayload,
} from "../../utils/helperFunctions/apiErrorHelper";
import { UpsertAdminPropertyRequest } from "../../models/RequestModels/UpsertAdminPropertyRequest";
import { UpsertAdminUnitRequest } from "../../models/RequestModels/UpsertAdminUnitRequest";

interface AdminAccountsState {
  accounts: AdminAccountResponse[];
  selectedAccount: AdminAccountResponse | null;
  subscriptions: AdminSubscriptionResponse[];
  pagination: Omit<PaginatedResponse<AdminAccountResponse>, "data"> | null;
  filters: AdminAccountFilters;
  loading: boolean;
  subscriptionsLoading: boolean;
  detailsLoading: boolean;
  saving: boolean;
  error: string | null;
  fieldErrors: FieldErrors;
}

const initialState: AdminAccountsState = {
  accounts: [],
  selectedAccount: null,
  subscriptions: [],
  pagination: null,
  filters: {
    page: 1,
    per_page: 20,
    search: "",
    status: "",
    plan: "",
  },
  loading: false,
  subscriptionsLoading: false,
  detailsLoading: false,
  saving: false,
  error: null,
  fieldErrors: {},
};

export const fetchAdminSubscriptions = createAsyncThunk(
  "adminAccounts/fetchAdminSubscriptions",
  async (_, { rejectWithValue }) => {
    try {
      return await getAdminSubscriptionsRequest();
    } catch (error: any) {
      return rejectWithValue(getApiErrorPayload(error));
    }
  },
);

export const fetchAdminAccounts = createAsyncThunk(
  "adminAccounts/fetchAdminAccounts",
  async (filters: AdminAccountFilters, { rejectWithValue }) => {
    try {
      return await getAdminAccountsRequest(filters);
    } catch (error: any) {
      return rejectWithValue(getApiErrorPayload(error));
    }
  },
);

export const fetchAdminAccount = createAsyncThunk(
  "adminAccounts/fetchAdminAccount",
  async (accountId: number, { rejectWithValue }) => {
    try {
      return await getAdminAccountRequest(accountId);
    } catch (error: any) {
      return rejectWithValue(getApiErrorPayload(error));
    }
  },
);

export const createAdminAccount = createAsyncThunk(
  "adminAccounts/createAdminAccount",
  async (body: CreateAdminAccountRequest, { rejectWithValue }) => {
    try {
      return await createAdminAccountRequest(body);
    } catch (error: any) {
      return rejectWithValue(getApiErrorPayload(error));
    }
  },
);

export const updateAdminSubscription = createAsyncThunk(
  "adminAccounts/updateAdminSubscription",
  async (
    payload: {
      accountId: number;
      body: UpdateAdminSubscriptionRequest;
    },
    { rejectWithValue },
  ) => {
    try {
      await updateAdminSubscriptionRequest(payload.accountId, payload.body);

      return await getAdminAccountRequest(payload.accountId);
    } catch (error: any) {
      return rejectWithValue(getApiErrorPayload(error));
    }
  },
);

export const createAdminCleaner = createAsyncThunk(
  "adminAccounts/createAdminCleaner",
  async (
    payload: { accountId: number; body: CreateAdminCleanerRequest },
    { rejectWithValue },
  ) => {
    try {
      await createAdminCleanerRequest(payload.accountId, payload.body);
      return await getAdminAccountRequest(payload.accountId);
    } catch (error: any) {
      return rejectWithValue(getApiErrorPayload(error));
    }
  },
);

export const saveAdminProperty = createAsyncThunk(
  "adminAccounts/saveAdminProperty",
  async (
    payload: {
      accountId: number;
      propertyId?: number;
      body: UpsertAdminPropertyRequest;
    },
    { rejectWithValue },
  ) => {
    try {
      if (payload.propertyId) {
        await updateAdminPropertyRequest(
          payload.accountId,
          payload.propertyId,
          payload.body,
        );
      } else {
        await createAdminPropertyRequest(payload.accountId, payload.body);
      }

      return await getAdminAccountRequest(payload.accountId);
    } catch (error: any) {
      return rejectWithValue(getApiErrorPayload(error));
    }
  },
);

export const saveAdminUnit = createAsyncThunk(
  "adminAccounts/saveAdminUnit",
  async (
    payload: {
      accountId: number;
      unitId?: number;
      body: UpsertAdminUnitRequest;
    },
    { rejectWithValue },
  ) => {
    try {
      if (payload.unitId) {
        await updateAdminUnitRequest(
          payload.accountId,
          payload.unitId,
          payload.body,
        );
      } else {
        await createAdminUnitRequest(payload.accountId, payload.body);
      }

      return await getAdminAccountRequest(payload.accountId);
    } catch (error: any) {
      return rejectWithValue(getApiErrorPayload(error));
    }
  },
);

export const updateAdminUserStatus = createAsyncThunk(
  "adminAccounts/updateAdminUserStatus",
  async (
    payload: { userId: number; isActive: boolean; accountId?: number },
    { rejectWithValue },
  ) => {
    try {
      await updateAdminUserStatusRequest(payload.userId, {
        is_active: payload.isActive,
      });

      if (payload.accountId) {
        return await getAdminAccountRequest(payload.accountId);
      }

      return null;
    } catch (error: any) {
      return rejectWithValue(getApiErrorPayload(error));
    }
  },
);

export const deleteAdminUser = createAsyncThunk(
  "adminAccounts/deleteAdminUser",
  async (
    payload: { userId: number; accountId?: number },
    { rejectWithValue },
  ) => {
    try {
      await deleteAdminUserRequest(payload.userId);

      if (payload.accountId) {
        return await getAdminAccountRequest(payload.accountId);
      }

      return null;
    } catch (error: any) {
      return rejectWithValue(getApiErrorPayload(error));
    }
  },
);

export const deleteAdminAccount = createAsyncThunk(
  "adminAccounts/deleteAdminAccount",
  async (accountId: number, { rejectWithValue }) => {
    try {
      await deleteAdminAccountRequest(accountId);
      return accountId;
    } catch (error: any) {
      return rejectWithValue(getApiErrorPayload(error));
    }
  },
);

const applyReject = (state: AdminAccountsState, action: any) => {
  const payload = action.payload as ApiRejectPayload | undefined;

  state.loading = false;
  state.subscriptionsLoading = false;
  state.detailsLoading = false;
  state.saving = false;
  state.error = payload?.message || "Request failed.";
  state.fieldErrors = payload?.fieldErrors || {};
};

const replaceAccountInList = (
  accounts: AdminAccountResponse[],
  account: AdminAccountResponse,
): AdminAccountResponse[] => {
  const exists = accounts.some((item) => item.id === account.id);

  if (!exists) {
    return [account, ...accounts];
  }

  return accounts.map((item) => (item.id === account.id ? account : item));
};

const adminAccountsSlice = createSlice({
  name: "adminAccounts",
  initialState,
  reducers: {
    setAdminAccountFilters: (state, action) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
      };
    },
    clearAdminAccountsError: (state) => {
      state.error = null;
      state.fieldErrors = {};
    },
    clearSelectedAccount: (state) => {
      state.selectedAccount = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAdminSubscriptions.pending, (state) => {
        state.subscriptionsLoading = true;
        state.error = null;
      })
      .addCase(fetchAdminSubscriptions.fulfilled, (state, action) => {
        state.subscriptionsLoading = false;
        state.subscriptions = Array.isArray(action.payload)
          ? action.payload
          : [];
      })
      .addCase(fetchAdminSubscriptions.rejected, applyReject)

      .addCase(fetchAdminAccounts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminAccounts.fulfilled, (state, action) => {
        const { data, ...pagination } = action.payload;

        state.loading = false;
        state.accounts = data;
        state.pagination = pagination;
      })
      .addCase(fetchAdminAccounts.rejected, applyReject)

      .addCase(fetchAdminAccount.pending, (state) => {
        state.detailsLoading = true;
        state.error = null;
      })
      .addCase(fetchAdminAccount.fulfilled, (state, action) => {
        state.detailsLoading = false;
        state.selectedAccount = action.payload;
      })
      .addCase(fetchAdminAccount.rejected, applyReject)

      .addCase(createAdminAccount.pending, (state) => {
        state.saving = true;
        state.error = null;
        state.fieldErrors = {};
      })
      .addCase(createAdminAccount.fulfilled, (state, action) => {
        state.saving = false;
        state.accounts = replaceAccountInList(state.accounts, action.payload);
        state.selectedAccount = action.payload;
      })
      .addCase(createAdminAccount.rejected, applyReject)

      .addCase(updateAdminSubscription.pending, (state) => {
        state.saving = true;
        state.error = null;
        state.fieldErrors = {};
      })
      .addCase(updateAdminSubscription.fulfilled, (state, action) => {
        state.saving = false;
        state.accounts = replaceAccountInList(state.accounts, action.payload);
        state.selectedAccount = action.payload;
      })
      .addCase(updateAdminSubscription.rejected, applyReject)

      .addCase(createAdminCleaner.pending, (state) => {
        state.saving = true;
        state.error = null;
        state.fieldErrors = {};
      })
      .addCase(createAdminCleaner.fulfilled, (state, action) => {
        state.saving = false;
        state.accounts = replaceAccountInList(state.accounts, action.payload);
        state.selectedAccount = action.payload;
      })
      .addCase(createAdminCleaner.rejected, applyReject)
      .addCase(saveAdminProperty.pending, (state) => {
        state.saving = true;
        state.error = null;
        state.fieldErrors = {};
      })
      .addCase(saveAdminProperty.fulfilled, (state, action) => {
        state.saving = false;

        state.accounts = replaceAccountInList(state.accounts, action.payload);

        state.selectedAccount = action.payload;
      })
      .addCase(saveAdminProperty.rejected, applyReject)

      .addCase(saveAdminUnit.pending, (state) => {
        state.saving = true;
        state.error = null;
        state.fieldErrors = {};
      })
      .addCase(saveAdminUnit.fulfilled, (state, action) => {
        state.saving = false;

        state.accounts = replaceAccountInList(state.accounts, action.payload);

        state.selectedAccount = action.payload;
      })
      .addCase(saveAdminUnit.rejected, applyReject)

      .addCase(updateAdminUserStatus.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(updateAdminUserStatus.fulfilled, (state, action) => {
        state.saving = false;

        if (action.payload) {
          state.accounts = replaceAccountInList(state.accounts, action.payload);
          state.selectedAccount = action.payload;
        }
      })
      .addCase(updateAdminUserStatus.rejected, applyReject)

      .addCase(deleteAdminUser.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(deleteAdminUser.fulfilled, (state, action) => {
        state.saving = false;

        if (action.payload) {
          state.accounts = replaceAccountInList(state.accounts, action.payload);
          state.selectedAccount = action.payload;
        }
      })
      .addCase(deleteAdminUser.rejected, applyReject)

      .addCase(deleteAdminAccount.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(deleteAdminAccount.fulfilled, (state, action) => {
        state.saving = false;
        state.accounts = state.accounts.filter(
          (account) => account.id !== action.payload,
        );

        if (state.selectedAccount?.id === action.payload) {
          state.selectedAccount = null;
        }
      })
      .addCase(deleteAdminAccount.rejected, applyReject);
  },
});

export const {
  setAdminAccountFilters,
  clearAdminAccountsError,
  clearSelectedAccount,
} = adminAccountsSlice.actions;

export default adminAccountsSlice.reducer;
